import type {
  DiagnosisModel,
  DiagnosisSearchResponseModel,
} from "../types/models";
import type {
  DiagnosisMutationsParams,
  DiagnosisSummaryParams,
} from "../types/params";

export type DiagnosisMutationsState = {
  add: DiagnosisSummaryParams[];
  remove_ids: string[];
  removed_existing_codes: string[];
};

export const hasMeaningfulDiagnosisQuery = (query: string, minLength = 2) => {
  return query.trim().length >= minLength;
};

export const mapBackendDiagnosesToSummary = (
  diagnoses: DiagnosisModel[]
): DiagnosisSummaryParams[] => {
  return (diagnoses || []).map((item) => ({
    id: item.id,
    icd_code: item.icd_code,
    title: item.title,
  }));
};

export const mapDiagnosisSearchResponse = (
  response: DiagnosisSearchResponseModel
): DiagnosisSummaryParams[] => {
  const seen = new Set<string>();
  const stripTags = (text: string) => text.replace(/<[^>]*>/g, "").trim();

  return (response?.items || [])
    .filter((item) => item.code && item.title)
    .map((item) => ({
      icd_code: item.code as string,
      title: stripTags(item.title as string),
    }))
    .filter((item) => {
      if (seen.has(item.icd_code)) {
        return false;
      }
      seen.add(item.icd_code);
      return true;
    });
};

export const mergeDiagnosesWithMutations = ({
  backendDiagnoses,
  routeSelectedDiagnoses,
  mutations,
}: {
  backendDiagnoses: DiagnosisSummaryParams[];
  routeSelectedDiagnoses: DiagnosisSummaryParams[];
  mutations: DiagnosisMutationsState;
}): DiagnosisSummaryParams[] => {
  const hasRouteSelection = routeSelectedDiagnoses.length > 0;
  const hasMutationState =
    mutations.add.length > 0 ||
    mutations.remove_ids.length > 0 ||
    mutations.removed_existing_codes.length > 0;

  if (!hasRouteSelection && !hasMutationState) {
    return backendDiagnoses;
  }

  const retainedExisting = backendDiagnoses.filter((diagnosis) => {
    const removedById = diagnosis.id
      ? mutations.remove_ids.includes(diagnosis.id)
      : false;
    const removedByCode = mutations.removed_existing_codes.includes(
      diagnosis.icd_code
    );
    return !removedById && !removedByCode;
  });

  const merged = [...retainedExisting];
  mutations.add.forEach((diagnosis) => {
    if (!merged.some((item) => item.icd_code === diagnosis.icd_code)) {
      merged.push(diagnosis);
    }
  });

  return merged;
};

export const buildDiagnosisMutationsPayload = (
  diagnosisMutations: DiagnosisMutationsState
): DiagnosisMutationsParams | undefined => {
  const seenAddCodes = new Set<string>();
  const add = diagnosisMutations.add
    .map((item) => item.icd_code)
    .filter((code) => {
      if (seenAddCodes.has(code)) {
        return false;
      }
      seenAddCodes.add(code);
      return true;
    })
    .map((icd_code) => ({ icd_code }));

  const remove_ids = Array.from(new Set(diagnosisMutations.remove_ids));

  if (add.length === 0 && remove_ids.length === 0) {
    return undefined;
  }

  return { add, remove_ids };
};
